import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import React, { useState, useEffect } from "react";
import "./cssNotifications.css";
import SingleNotification from "./SingleNotification.jsx";
import { useSelector, useDispatch } from "react-redux";
import {
  getNotificationByEmail,
  clearAllNotificationsByUserId,
  deleteNotificationById,
} from "../../../slices/notificationSlice.js";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Button,
  Box,
  Modal,
  Typography,
  Snackbar,
  Alert,
  useTheme,
  Tooltip,
} from "@mui/material";

function Notifications({ embedded }) {
  const email = sessionStorage?.getItem("email");
  const userId = sessionStorage?.getItem("userId");
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(true);

  let notificationsResult = useSelector(
    (state) => state?.notification?.notifications
  );

  notificationsResult = [...notificationsResult]?.sort(
    (a, b) => b?.createdOn - a?.createdOn
  );

  useEffect(() => {
    if (!email) return;

    setLoading(true);
    dispatch(getNotificationByEmail(email)).finally(() => {
      setLoading(false);
    });

    if (!embedded) {
      setShowBackdrop(true);
      setShowPanel(true);
    }

    const intervalId = setInterval(() => {
      dispatch(getNotificationByEmail(email));
    }, 60000);

    return () => clearInterval(intervalId);
  }, [email, embedded, dispatch]);

  const handleOpen = (e) => {
    if (e) e.stopPropagation();
    setOpen(true);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setOpen(false);
  };

  const closeBackdrop = () => {
    if (!embedded) {
      setShowBackdrop(false);
      setShowPanel(false);
    }
  };

  const closeBackdropAndNavigate = (goToRoute) => {
    if (goToRoute === `support`) {
      sessionStorage.setItem("admMenuSelected", "Support");
    } else if (goToRoute === `adApprovallist`) {
      sessionStorage.setItem("admMenuSelected", "Ad Approval List");
    } else if (goToRoute === `viewersList`) {
      sessionStorage.setItem("admMenuSelected", "Viewers List");
    } else if (goToRoute === `requests/refundrequests`) {
      sessionStorage.setItem("admMenuSelected", "Refund Requests");
    }
    
    if (!embedded) {
      setShowBackdrop(false);
      setShowPanel(false);
    }
    navigate(goToRoute);
  };

  const handleOkDelete = (e) => {
    if (e) e.stopPropagation();
    
    if (!userId) {
      setSnackbarMessage("Error: User ID not found");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setOpen(false);
      return;
    }
    
    dispatch(clearAllNotificationsByUserId(userId)).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getNotificationByEmail(email));
        setSnackbarMessage("Notifications cleared successfully!");
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage("Failed to clear notifications");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    }).catch((error) => {
      setSnackbarMessage("Error clearing notifications");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    });
    setOpen(false);
  };

  const clearNotification = (id) => {
    dispatch(deleteNotificationById(id));
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const renderPanelContent = () => (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          padding: "16px 20px",
          zIndex: 2,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
          backdropFilter: 'blur(20px)'
        }}
      >
        <Typography
          variant="h6"
          sx={{ 
            margin: 0, 
            fontSize: "1.1rem", 
            fontWeight: 700,
            color: 'text.primary',
            letterSpacing: '-0.2px'
          }}
        >
          Notifications
        </Typography>
        {notificationsResult?.length >= 1 && (
          <Button
            variant="text"
            size="small"
            sx={{
              color: 'primary.main',
              padding: "4px 8px",
              borderRadius: "8px",
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "none",
              '&:hover': {
                backgroundColor: 'primary.main' + '10',
              },
            }}
            onClick={handleOpen}
          >
            Clear All
          </Button>
        )}
      </Box>
      <Box sx={{ py: 1 }}>
        {loading ? (
          <Box
            sx={{
              height: !embedded ? "40vh" : "160px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={28} thickness={5} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <>
            {notificationsResult?.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {notificationsResult?.map((notification, index) => {
                  let route = "";
                  if (notification?.redirectKey === "checkSupport") route = `support`;
                  else if (notification?.redirectKey === "checkSupportAdvertiser") route = `support/advertisersupport`;
                  else if (notification?.redirectKey === "checkSupportViewer") route = `support/viewersupport`;
                  else if (notification?.redirectKey === "checkAd") route = `adApprovallist`;
                  else if (notification?.redirectKey === "checkRedeemRequest") route = `viewersList`;
                  else if (notification?.redirectKey === "checkRefundRequest") route = `requests/refundrequests`;
                  
                  return (
                    <SingleNotification
                      key={notification.id || index}
                      message={notification?.message}
                      time={notification?.createdOn}
                      navigate={() => closeBackdropAndNavigate(route)}
                      onClose={() => clearNotification(notification.id)}
                    />
                  );
                })}
              </Box>
            )}

            {notificationsResult?.length === 0 && (
              <Box 
                sx={{ 
                  color: "text.secondary", 
                  padding: "60px 20px",
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  textAlign: 'center'
                }}
              >
                <Box 
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '50%', 
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <NotificationsOutlinedIcon sx={{ fontSize: 24, opacity: 0.5 }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  All caught up!
                </Typography>
                <Typography variant="caption" sx={{ maxWidth: '200px', opacity: 0.7 }}>
                  No notifications yet.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );

  return (
    <>
      <div className="notification-content-container">
        {(showBackdrop || embedded) && (
          <>
            {!embedded && <div className="backdrop" onClick={closeBackdrop}></div>}
            <Box
              className={`notification-panel ${showPanel || embedded ? "" : "hidden"}`}
              sx={{ 
                  padding: "0px",
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                  
                  ...(embedded ? { 
                      position: 'static', 
                      width: '100%', 
                      maxHeight: 'none', 
                      top: 'auto', 
                      right: 'auto', 
                      boxShadow: 'none',
                      animation: 'none',
                      border: 'none'
                  } : {
                    boxShadow: "0 12px 48px -12px rgba(0, 0, 0, 0.7)",
                    borderRadius: "16px",
                  })
              }}
            >
              {renderPanelContent()}
            </Box>
          </>
        )}
      </div>

      <Modal
        open={open}
        onClose={(_event, _reason) => handleClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px 30px",
            outline: "none",
            borderRadius: "12px",
            width: { xs: "90%", sm: "400px" },
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: 'blur(10px)',
            zIndex: 1400
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{ textAlign: "center", mb: 3 }}
          >
            Are you sure to delete all notifications?
          </Typography>
          <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "center" }}>
            <Button
              onClick={handleOkDelete}
              variant="contained"
              sx={{
                flex: 1,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: theme.palette.primary.dark },
              }}
            >
              Confirm
            </Button>
            <Button
              onClick={handleClose}
              sx={{
                flex: 1,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                textTransform: "none",
                "&:hover": {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          elevation={6}
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Notifications;
